import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../context/CartContext";

// Hardcoded 30-day launch banner. The deploy_date is the day the banner
// was shipped; expiry = deploy_date + 30 days. After that the component
// returns null so the banner disappears without a redeploy needed.
//
// To restart the countdown, change GRAND_OPENING_START_ISO below.
const GRAND_OPENING_START_ISO = "2026-07-14";
const GRAND_OPENING_DURATION_DAYS = 30;
const GRAND_OPENING_CODE = "GRANDOPENING";

const MESSAGES = [
  {
    text: "Grand Opening — use code GRANDOPENING for 5% off",
    action: "claim",   // clicking auto-applies + copies GRANDOPENING
  },
  {
    text: "New customers get 10% off their first order with code FIRST10",
    action: "copy",    // clicking just copies FIRST10 to clipboard
    code: "FIRST10",
  },
];

const isExpired = () => {
  const start = new Date(GRAND_OPENING_START_ISO + "T00:00:00Z");
  const end = new Date(start.getTime() + GRAND_OPENING_DURATION_DAYS * 24 * 3600 * 1000);
  return Date.now() > end.getTime();
};

const GrandOpeningBanner = () => {
  const { addPromoCode } = useCart();
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState("");
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("luviarx_grand_opening_dismissed_v1") === "1";
    } catch {
      return false;
    }
  });
  const expired = useMemo(isExpired, []);

  useEffect(() => {
    if (expired || dismissed) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % MESSAGES.length), 5000);
    return () => clearInterval(t);
  }, [expired, dismissed]);

  if (expired || dismissed) return null;

  const active = MESSAGES[idx];

  const handleAction = async () => {
    if (active.action === "claim") {
      // Auto-apply GRANDOPENING to cart. It'll validate on the server the
      // first time the customer opens the checkout Review page, but we
      // still add it locally so the totals recompute immediately.
      addPromoCode(GRAND_OPENING_CODE, 5, 0);
      try {
        await navigator.clipboard?.writeText(GRAND_OPENING_CODE);
      } catch { /* ignore clipboard errors */ }
      setCopied("Applied to your cart ✓");
    } else if (active.action === "copy" && active.code) {
      try {
        await navigator.clipboard?.writeText(active.code);
      } catch { /* ignore */ }
      setCopied(`Copied ${active.code} ✓`);
    }
    setTimeout(() => setCopied(""), 2000);
  };

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem("luviarx_grand_opening_dismissed_v1", "1");
    } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="grand-opening-banner"
      className="bg-[#8B2252] text-cream-100 text-xs sm:text-sm py-3 relative overflow-hidden"
    >
      <div className="container-x flex items-center justify-center gap-3 sm:gap-4 px-8 sm:px-12">
        <span
          data-testid="grand-opening-message"
          key={idx}
          className="text-center font-medium tracking-wide"
          style={{ animation: "fadeIn 0.6s ease" }}
        >
          {active.text}
        </span>
        <button
          type="button"
          onClick={handleAction}
          data-testid={`grand-opening-${active.action}`}
          className="shrink-0 inline-flex items-center bg-cream-100 text-[#8B2252] font-display font-bold text-[11px] uppercase tracking-[0.14em] px-3 py-1 rounded-full hover:bg-cream-200 transition-colors"
        >
          {copied || (active.action === "claim" ? "Claim" : "Copy")}
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="grand-opening-dismiss"
        aria-label="Dismiss announcement"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-cream-100/70 hover:text-cream-100 text-lg leading-none w-6 h-6 flex items-center justify-center"
      >
        ×
      </button>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
};

export default GrandOpeningBanner;
